import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useState, useEffect, useContext } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import DateRangePicker from '../../components/shared/date-range-picker';
import Modal from '../../components/shared/modal';
import { AccountsContext } from '../../components/context/account-context';
import { getUsersMinimal } from '../../services/users-service';
import { getVehiclesMinimal } from '../../services/vehicles-service';
import { getAllVendorsMinimal } from '../../services/vendors-service';
import logo from '../../assets/images/logo.jpg';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDateRangeUTC, formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { format } from 'date-fns';
import { getAllServiceRTypes, getAllServices, getAllServiceStatuses } from '../../services/services-service';
import ServicesTables from './services-table';
import AddModal from './add-modal';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Services = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [serviceData, setServiceData] = useState([]);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalDataloading, setGlobalDataLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});


    const fetchGlobalData = async () => {
        if (!accountId) return;
        setGlobalDataLoading(true);

        try {
            const [usersRes, vehiclesRes, vendorsRes, statusOptionRes, typesRes] = await Promise.all([
                getUsersMinimal(accountId),
                getVehiclesMinimal(accountId),
                getAllVendorsMinimal(accountId),
                getAllServiceStatuses(accountId),
                getAllServiceRTypes(accountId),
            ]);

            if (usersRes.success) setUsers(usersRes.data.data || []);
            if (vehiclesRes.success) setVehicles(vehiclesRes.data.data || []);
            if (vendorsRes.success) setVendors(vendorsRes.data.data || []);
            if (statusOptionRes.success) setStatusOptions(statusOptionRes.data.data || []);
            if (typesRes.success) setTypeOptions(typesRes.data.data || []);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setGlobalDataLoading(false);
        }
    };

    const fetchServices = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);

            const response = await getAllServices(accountId, from, to);
            console.log(response)

            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setServiceData(sortedData);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch Service records');
            }
        } catch (err) {
            setError('An error occurred while fetching Service records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, [accountId, dateRange]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchServices();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? serviceData.filter((_, index) => selectedRows[index])
        : serviceData;

    const csvData = exportData.map(item => ({
        Vehicle: item.vehicle?.name || '',
        Vendor: item.vendor?.name || 'Unknown',
        Type: Array.isArray(item.types) ? item.types.join(', ') : item.types || '',
        'Service Time': formatUTCTimestampToLocal(item.serviceTime, 'yyyy-MM-dd HH:mm'),
        Status: item.status || '',
        Note: item.note || '',
    }));

    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title placement
        const title = 'Services Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Date range placement
        const start = format(dateRange.startDate, 'yyyy-MM-dd');
        const end = format(dateRange.endDate, 'yyyy-MM-dd');
        const dateRangeText = `Date Range: ${start} to ${end}`;
        const dateRangeY = titleY + 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateRangeWidth = doc.getTextWidth(dateRangeText);
        const dateRangeX = (pageWidth - dateRangeWidth) / 2;
        doc.text(dateRangeText, dateRangeX, dateRangeY);

        // Table
        autoTable(doc, {
            head: [['Vehicle', 'Vendor', 'Type', 'Service Time', 'Status', 'Note']],
            body: exportData.map(item => [
                item.vehicle?.name || '',
                item.vendor?.name || 'Unknown',
                Array.isArray(item.types) ? item.types.join(', ') : item.types || '',
                formatUTCTimestampToLocal(item.serviceTime, 'yyyy-MM-dd HH:mm'),
                item.status || '',
                item.note || '',
            ]),
            startY: dateRangeY + 5,
        });

        // Format filename with date range
        const fileName = `services-${start}-to-${end}.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div>
                    <Heading>Service History</Heading>
                </div>
                <Button
                    label='Add Service'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                />
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <DateRangePicker
                            onChange={setDateRange}
                            initialRange={{
                                startDate: dateRange.startDate,
                                endDate: dateRange.endDate,
                            }}
                        />
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={`Services-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <ServicesTables
                    data={serviceData}
                    setData={setServiceData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchServices={fetchServices}
                    loadingData={loading}
                    accountId={accountId}
                    spendors={users}
                    vendors={vendors}
                    vehicles={vehicles}
                    statusOptions={statusOptions}
                    types={typeOptions}
                    fetchGlobalData={fetchGlobalData}
                    globalDataloading={globalDataloading}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        vendors={vendors}
                        vehicles={vehicles}
                        types={typeOptions}
                        fetchGlobalData={fetchGlobalData}
                        globalDataloading={globalDataloading}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default Services;